import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import PropTypes from 'prop-types';
import invariant from 'react-utils/invariant';
import emptyFunction from 'react-utils/emptyFunction';
import TourContext from '../contexts/TourContext';
import { TourTrackingContext } from '../contexts/TourTrackingContext';
import MultiTourHandlerSingleton from '../lib/MultiTourHandlerSingleton';
import MultiTourHandler from '../lib/MultiTourHandler';

class UITour extends Component {
  constructor(props) {
    super(props);
    this.state = {};
    this._mounted = false;
    this.unsubscribes = [];
    this.startTour = this.startTour.bind(this);
    this.subscribeToTour = this.subscribeToTour.bind(this);
    this.getTourHandler = this.getTourHandler.bind(this);
    this.registerTours();
  }

  componentDidMount() {
    this._mounted = true;
    const {
      tour,
      activateOnMount,
      multiTourHandler
    } = this.props;

    if (activateOnMount) {
      const runningTourKey = multiTourHandler.getRunningTourKey();
      const hasOtherRunningTour = runningTourKey && runningTourKey !== tour; // activate the tour if no other tour running

      if (!hasOtherRunningTour) {
        this.activateTour();
      }
    }
  }

  componentDidUpdate(prevProps) {
    const {
      keepAlive,
      tour
    } = this.props;
    const {
      tour: prevTour
    } = prevProps;

    if (!keepAlive || !prevTour || !tour || prevTour === tour) {
      return;
    }

    const {
      autoStartOnUpdate,
      config,
      multiTourHandler
    } = this.props;
    const {
      progressStore
    } = multiTourHandler.getTour(prevTour);
    multiTourHandler.deactivateTour(prevTour);
    multiTourHandler.registerTour(tour, config, progressStore);
    multiTourHandler.activateTour(tour);

    if (autoStartOnUpdate) {
      this.startTour();
    }
  }

  componentWillUnmount() {
    const {
      tour,
      tours
    } = this.props;
    this._mounted = false;

    if (tour) {
      this.deregisterTour(tour);
    } else if (tours) {
      tours.forEach(tourKey => {
        this.deregisterTour(tourKey);
      });
    }

    this.unsubscribes.forEach(unsubscribe => unsubscribe());
  }

  getContextValue() {
    const {
      config,
      multiTourHandler
    } = this.props;
    const getHandler = this.getTourHandler;
    return {
      tour: {
        getHandler,
        getConfig: () => config || getHandler().getConfig(),
        getStep: () => getHandler().getStepKey(),
        getTour: () => multiTourHandler.getActiveTourKey(),
        deactivate: callback => multiTourHandler.deactivate(callback),
        deactivateTour: tourKey => {
          if (tourKey) {
            multiTourHandler.deactivateTour(tourKey);
          }
        },
        activate: (tourKey = this.getDefaultTour()) => multiTourHandler.activateTour(tourKey),
        canGoBack: () => getHandler().canGoBack(),
        subscribe: this.subscribeToTour,
        start: this.startTour,
        next: stepKey => getHandler().nextStep(stepKey),
        back: () => getHandler().goBack(),
        finish: chainable => getHandler().finishTour(chainable)
      }
    };
  }

  registerTours() {
    const {
      tours,
      tour
    } = this.props;

    if (tour) {
      this.registerTour(tour);
    } else if (tours) {
      tours.forEach(tourKey => {
        this.registerTour(tourKey);
      });
    }
  }

  registerTour(tourKey) {
    const {
      config,
      multiTourHandler
    } = this.props;
    const tourHandler = multiTourHandler.registerTour(tourKey, config);
    const unsubscribe = tourHandler.subscribeToUpdates((nextStep, nextTour) => {
      if (this._mounted) {
        this.setState({
          tour: nextTour,
          step: nextStep
        });
      }
    });
    this.unsubscribes = this.unsubscribes ? this.unsubscribes.concat(unsubscribe) : [unsubscribe];
  }

  deregisterTour(tourKey) {
    const {
      multiTourHandler
    } = this.props;

    if (!tourKey) {
      return;
    }

    if (typeof multiTourHandler.deregisterTour === 'function') {
      multiTourHandler.deregisterTour(tourKey);
    } else {
      multiTourHandler.deactivateTour(tourKey);
    }
  }

  activateTour() {
    const {
      multiTourHandler,
      onActivate
    } = this.props;
    const defaultTour = this.getDefaultTour();
    multiTourHandler.activateTour(defaultTour, onActivate);
  }

  startTour(tourKey, chainable) {
    const {
      multiTourHandler
    } = this.props;

    const startTour = () => {
      const trackingContext = this.context;
      const {
        tourTrackingObserver
      } = trackingContext || {};

      if (tourTrackingObserver) {
        const unsubscribe = this.subscribeToTour(tourTrackingObserver.observe);
        this.unsubscribes = this.unsubscribes.concat(unsubscribe);
      }

      return this.getTourHandler().startTour(chainable);
    };

    if (tourKey && tourKey !== multiTourHandler.getActiveTourKey()) {
      return multiTourHandler.activateTour(tourKey, startTour);
    }

    return startTour();
  }

  subscribeToTour(callback) {
    const {
      tours,
      tour,
      multiTourHandler
    } = this.props;

    if (tour) {
      const handler = multiTourHandler.getTour(tour);
      return [handler.subscribeToUpdates(callback)];
    }

    return tours.map(tourKey => {
      const handler = multiTourHandler.getTour(tourKey);
      return handler.subscribeToUpdates(callback);
    });
  }

  getDefaultTour() {
    const {
      tours,
      tour
    } = this.props;
    invariant(tour || tours && tours.length > 0, 'You must supply at least a tour (tour key) or a tours (array of tour keys) to `UITour` props');
    return tour || tours[0];
  }

  getTourHandler() {
    const {
      multiTourHandler
    } = this.props;
    const activeTour = multiTourHandler.getActiveTour();

    if (activeTour) {
      return activeTour;
    }

    const defaultTour = this.getDefaultTour();
    return multiTourHandler.getTour(defaultTour);
  }

  render() {
    const {
      children,
      activateOnMount,
      className
    } = this.props; // Not render context when tour is not registered in componentDidMount

    if (activateOnMount && !this._mounted) {
      return null;
    }

    return /*#__PURE__*/_jsx("div", {
      className: className,
      children: /*#__PURE__*/_jsx(TourContext.Provider, {
        value: this.getContextValue(),
        children: children
      })
    });
  }

}

UITour.contextType = TourTrackingContext;
UITour.propTypes = {
  tour: PropTypes.string,
  tours: PropTypes.array,
  config: PropTypes.object,
  children: PropTypes.node.isRequired,
  multiTourHandler: PropTypes.instanceOf(MultiTourHandler),
  onActivate: PropTypes.func.isRequired,
  activateOnMount: PropTypes.bool.isRequired,
  autoStartOnUpdate: PropTypes.bool,
  keepAlive: PropTypes.bool,
  className: PropTypes.string
};
UITour.defaultProps = {
  onActivate: emptyFunction,
  multiTourHandler: MultiTourHandlerSingleton.getInstance(),
  activateOnMount: true,
  keepAlive: false
};
export default UITour;